import React, { useState, useRef } from "react";
import { createEvent, updateEvent } from "../../../Services/Event";
import { IEvent } from "../../../interfaces/models/IEvent";
import { getFirmByIdOrLegacyId } from "../../../Services/Firm";
import { EVENT_DATA_REGISTRY } from "./data";
import { LegacyEventData } from "./data/LegacyEventData";
import { EventTypeEnum, StatusEnum, SetupTypeEnum, EventHostSponsorEnum } from "../../../Enums/EventEnums";

/**
 * Generic component to insert legacy event data
 * Allows selecting which event data to insert
 */
const LegacyEventInserter: React.FC = () => {
  const [selectedEventId, setSelectedEventId] = useState<string>(EVENT_DATA_REGISTRY[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEvents, setCreatedEvents] = useState<IEvent[]>([]);
  const [updatedEvents, setUpdatedEvents] = useState<IEvent[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [updateMode, setUpdateMode] = useState(false); // Toggle between insert and update mode
  
  // Get selected event data
  const selectedEvent = EVENT_DATA_REGISTRY.find(e => e.id === selectedEventId);
  const legacyRecords = selectedEvent?.records || [];

  /**
   * Converts legacy date string to Unix timestamp (seconds)
   * Format: "2025-05-20 17:00:00"
   */
  const convertDateToTimestamp = (dateString: string | null | undefined): number | undefined => {
    if (!dateString) {
      return undefined;
    }
    try {
      const date = new Date(dateString);
      return Math.floor(date.getTime() / 1000);
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return undefined;
    }
  };

  /**
   * Maps legacy event type to modern eventType
   */
  const mapEventType = (legacyType: string | null | undefined): "inPerson" | "virtual" | "hybrid" => {
    if (!legacyType) {
      return "inPerson";
    }
    const normalized = legacyType.trim().toLowerCase();
    if (normalized.includes("virtual") || normalized.includes("online")) {
      return "virtual";
    }
    if (normalized.includes("hybrid")) {
      return "hybrid";
    }
    return "inPerson";
  };

  /**
   * Maps legacy setup type to modern setupType
   */
  const mapSetupType = (legacyType: string | null | undefined): string | undefined => {
    if (!legacyType) {
      return undefined;
    }
    const normalized = legacyType.trim().toLowerCase();
    
    // Map common legacy types to modern setup types
    if (normalized.includes("cocktail") || normalized.includes("reception")) {
      return "cocktail";
    }
    if (normalized.includes("regional")) {
      return "regional";
    }
    if (normalized.includes("annual")) {
      return "annual";
    }
    if (normalized.includes("luncheon")) {
      return "luncheon";
    }
    if (normalized.includes("group")) {
      return "group";
    }
    if (normalized.includes("seminar")) {
      return "seminar";
    }
    if (normalized.includes("networking")) {
      return "networking";
    }
    if (normalized.includes("webinar")) {
      return "webinar";
    }
    
    return "general";
  };

  /**
   * Maps legacy host type to modern eventHostSponsor
   */
  const mapEventHostSponsor = (legacyHost: string | null | undefined): keyof typeof EventHostSponsorEnum => {
    if (!legacyHost) {
      return "wsg";
    }
    const normalized = legacyHost.trim().toLowerCase();
    
    if (normalized.includes("member")) {
      return "member";
    }
    if (normalized.includes("group")) {
      return "group";
    }
    if (normalized.includes("industry")) {
      return "industry";
    }
    if (normalized.includes("guest")) {
      return "guest";
    }
    
    return "wsg";
  };

  /**
   * Maps legacy status to modern status
   */
  const mapStatus = (isOffline: number | null | undefined, isHome: number | null | undefined): keyof typeof StatusEnum => {
    if (isOffline === 1) {
      return "offline";
    }
    if (isHome === 1) {
      return "online";
    }
    return "online";
  };

  /**
   * Creates event from legacy data record
   */
  const createLegacyEvent = async (
    legacyData: LegacyEventData
  ): Promise<IEvent> => {
    const meeting = legacyData.meetingDetails;
    const regSettings = legacyData.registrationSettings;

    // Convert dates
    const startDate = convertDateToTimestamp(meeting.datefrom);
    const endDate = convertDateToTimestamp(meeting.dateTo);
    const registrationExpiration = regSettings?.ExpirationDate 
      ? convertDateToTimestamp(`${regSettings.ExpirationDate} 23:59:59`)
      : undefined;

    // Map event type
    const eventType = mapEventType(meeting.EventType);
    const setupType = mapSetupType(meeting.SetupType || meeting.MType);
    const eventHostSponsor = mapEventHostSponsor(meeting.HostType);
    const status = mapStatus(meeting.IsOffline, meeting.IsHome);

    // Build address
    const address = {
      address1: meeting.MAddress || undefined,
      address2: meeting.MAddress2 || undefined,
      cityName: meeting.MCity || undefined,
      stateName: meeting.MState || undefined,
      zipCode: meeting.MZip || undefined,
      countryName: meeting.MCountry || undefined,
    };

    // Build tabs (default tabs for events)
    const tabs = [
      { type: "overview" as const, index: 0 },
      { type: "registration" as const, index: 1 },
      { type: "accomodation" as const, index: 2 },
      { type: "attendees" as const, index: 3 },
      { type: "contactUs" as const, index: 4 },
    ];

    // Build wait list settings
    const waitList = {
      eventCapacity: regSettings?.MaxEventCapacity || 0,
      firmCapacity: regSettings?.MaxFirmCapacity || 0,
      waitListMessage: regSettings?.WaitlistMessage || "Thank you for your interest. You have been added to the wait list. The WSG events team will reach out once a registration spot becomes available.",
      capacityMessage: regSettings?.FullCapacityMessage || "Due to limited capacity, registrations will be taken on a first come first serve basis. The limit to this event has been reached. If you are still interested in attending the event please join the waitlist. All waitlisted will not be charged until registration is confirmed.",
      disclaimCapacityMessage: regSettings?.DisclaimCapacityMessage || "Due to limited capacity, registrations will be taken on a first come first serve basis, initially limited to two registrants per firm. All others will be waitlisted and will not be charged until registration is confirmed.",
    };

    // Build fee structure
    const fee = regSettings ? {
      currency: regSettings.Currency || "USD",
      memberFee: regSettings.rprice1 || undefined,
      lateFee: regSettings.rprice3 ?? regSettings.rprice2 ?? undefined,
      cancellationPolicy: regSettings.RegTerms || undefined,
      partialRefund: regSettings.RefundPercent || undefined,
      fullRefundEndDate: regSettings.CancelStartDate 
        ? convertDateToTimestamp(regSettings.CancelStartDate)
        : undefined,
      partialRefundStartDate: regSettings.CancelStartDate 
        ? convertDateToTimestamp(regSettings.CancelStartDate)
        : undefined,
      partialRefundEndDate: regSettings.CancelEndDate 
        ? convertDateToTimestamp(regSettings.CancelEndDate)
        : undefined,
      noRefundDate: regSettings.CancelEndDate 
        ? convertDateToTimestamp(regSettings.CancelEndDate)
        : undefined,
    } : undefined;

    // Build accommodation if hotel info exists
    const accomodation = meeting.MHotelName ? [{
      primary: true,
      name: meeting.MHotelName,
      address: meeting.MHotelAddress || "",
      url: meeting.MHotelURL || "",
      reservationUrl: undefined,
      image: meeting.MHotelPicture || "",
      description: meeting.MHotelText || "",
      rate: meeting.MHotelRate || undefined,
    }] : undefined;

    // Build registration settings
    const registrationSettings = {
      waitListEnabled: regSettings?.HasWaitList === 1,
      feesEnabled: !!fee,
      registrationExpirationEnabled: regSettings?.HasExpirationDate === 1,
    };

    // Resolve meeting hosts (MeetingHosts table) to sponsorHost via firm legacy ID
    const sponsorHost: { type: "member"; id: string }[] = [];
    if (legacyData.meetingHosts?.length) {
      for (const host of legacyData.meetingHosts) {
        const firm = await getFirmByIdOrLegacyId(host.wsgCompaniesID);
        if (firm?.id) {
          sponsorHost.push({ type: "member", id: firm.id });
        }
      }
    }

    const event: IEvent = {
      eventType,
      setupType: setupType as any,
      title: meeting.MTitle || "Untitled Event",
      description: meeting.MDescription || meeting.LongDescription || "",
      overviewTabDescription: meeting.OverviewTabDescription || meeting.MDescription || meeting.LongDescription || "",
      startDate,
      endDate,
      timeZone: meeting.TimeZone || undefined,
      comingSoonDate: meeting.ComingSoon === 1,
      comingSoonDateMessage: meeting.ComingSoonMessage || undefined,
      comingSoonLocation: meeting.LocationComingSoon === 1,
      comingSoonLocationMessage: meeting.LocationComingSoonMessage || undefined,
      location: meeting.MLocation || meeting.MAddress || "",
      venueLink: meeting.VenueLink || "",
      address,
      eventHostSponsor: eventHostSponsor as any,
      sponsorHost,
      bannerLists: meeting.BannerUpcomingURL || undefined,
      bannerDetails: meeting.bannerURL || undefined,
      bannerHomePage: meeting.BannerHomePageURL || undefined,
      bannerInvoice: meeting.Emailbanner || undefined,
      status: status as any,
      tabs,
      regionIds: [], // Will need to be populated separately if needed
      homePage: meeting.IsHome === 1,
      upcomingEventPage: meeting.UpcomingEventPage === 1,
      waitList,
      fee,
      contactUs: [], // Will need to be populated separately if needed
      accomodation,
      registrationMessage: regSettings?.RegistrationMessage || undefined,
      registrationExpiration,
      registrationSettings,
      isDeleted: meeting.IsDeleted === 1,
      createdDate: meeting.DateCreated 
        ? convertDateToTimestamp(meeting.DateCreated)
        : Math.floor(Date.now() / 1000),
    };

    return event;
  };

  /**
   * Handles the insertion or update of all legacy events
   */
  const handleInsertAllLegacy = async () => {
    if (!selectedEvent || legacyRecords.length === 0) {
      setError('Please select an event group with event data');
      return;
    }

    setLoading(true);
    setSuccess(false);
    setError(null);
    setCreatedEvents([]);
    setUpdatedEvents([]);
    setProgress({ current: 0, total: legacyRecords.length });

    try {
      const results: IEvent[] = [];
      const errors: Array<{ meetingId: number; error: string }> = [];

      // Process each record
      for (let i = 0; i < legacyRecords.length; i++) {
        const record = legacyRecords[i];
        setProgress({ current: i + 1, total: legacyRecords.length });

        try {
          if (updateMode) {
            // Update existing event (would need event ID mapping)
            // For now, we'll skip update mode or you can implement ID mapping
            errors.push({
              meetingId: record.meetingDetails.MeetingID,
              error: "Update mode not yet implemented - requires event ID mapping"
            });
            continue;
          } else {
            // Create new event
            const event = await createLegacyEvent(record);
            const response = await createEvent(event);
            results.push(response);
          }
        } catch (err: any) {
          console.error(`Error ${updateMode ? 'updating' : 'creating'} event for MeetingID ${record.meetingDetails.MeetingID}:`, err);
          errors.push({
            meetingId: record.meetingDetails.MeetingID,
            error: err?.response?.data?.error ||
                   err?.response?.data?.message ||
                   err?.message ||
                   'Unknown error'
          });
        }
      }

      if (updateMode) {
        setUpdatedEvents(results);
      } else {
        setCreatedEvents(results);
      }
      
      if (results.length > 0) {
        setSuccess(true);
      }
      
      if (errors.length > 0) {
        setError(`${errors.length} event(s) failed. Check console for details.`);
        console.error('Failed events:', errors);
      }
      
      // Reset success message after 15 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 15000);
    } catch (err: any) {
      console.error('Error processing legacy events:', err);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to process events. Please check the console for details.'
      );
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="w-6/12 mx-auto p-5 border border-gray-300 shadow-sm shadow-black my-12 rounded-lg">
      <div className="w-full mx-auto">
        <h1 className="text-center text-lg my-3 font-semibold">Legacy Event {updateMode ? 'Updater' : 'Inserter'}</h1>
        
        <div className="w-full mb-4 flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={updateMode}
              onChange={(e) => setUpdateMode(e.target.checked)}
              disabled={loading}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-gray-700">
              Update Mode (modify existing events)
            </span>
          </label>
        </div>

        {updateMode && (
          <div className="w-full mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              <strong>Update Mode:</strong> This feature requires event ID mapping and is not yet fully implemented.
            </p>
          </div>
        )}
        
        <div className="w-full mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Event Group <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading || EVENT_DATA_REGISTRY.length === 0}
          >
            {EVENT_DATA_REGISTRY.length === 0 ? (
              <option value="">No event groups available</option>
            ) : (
              EVENT_DATA_REGISTRY.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} ({event.records.length} events)
                </option>
              ))
            )}
          </select>
        </div>

        {selectedEvent && (
          <>
            <p className="text-center text-sm text-gray-600 mb-4">
              This will {updateMode ? 'update' : 'insert'} {legacyRecords.length} legacy event record(s) for <strong>{selectedEvent.name}</strong>.
            </p>
            
            {loading && progress.total > 0 && (
              <div className="w-full mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-700">
                    Processing: {progress.current} / {progress.total} events
                  </p>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="w-full flex justify-center align-middle mb-4">
              <button
                type="button"
                onClick={handleInsertAllLegacy}
                disabled={loading || legacyRecords.length === 0}
                className={`w-fit border border-black rounded-md px-5 py-2 my-2 mx-auto ${
                  loading || legacyRecords.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {loading ? `Processing... (${progress.current}/${progress.total})` : `${updateMode ? 'Update' : 'Insert'} All ${legacyRecords.length} Events`}
              </button>
            </div>

            {error && (
              <div className="w-full mx-auto flex align-middle justify-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {success && (updateMode ? updatedEvents.length > 0 : createdEvents.length > 0) && (
              <div className="w-full mx-auto bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                <p className="text-sm font-semibold mb-2">
                  Successfully {updateMode ? 'Updated' : 'Created'} {updateMode ? updatedEvents.length : createdEvents.length} Event(s)!
                </p>
                <div className="text-xs mt-2 space-y-1 max-h-60 overflow-y-auto">
                  <p><strong>Total Processed:</strong> {legacyRecords.length}</p>
                  <p><strong>Successfully {updateMode ? 'Updated' : 'Created'}:</strong> {updateMode ? updatedEvents.length : createdEvents.length}</p>
                  <p><strong>Failed:</strong> {legacyRecords.length - (updateMode ? updatedEvents.length : createdEvents.length)}</p>
                  {(updateMode ? updatedEvents : createdEvents).length <= 10 && (
                    <div className="mt-2">
                      <p className="font-semibold">{updateMode ? 'Updated' : 'Created'} Events:</p>
                      {(updateMode ? updatedEvents : createdEvents).map((event, idx) => (
                        <p key={idx} className="ml-2">
                          • {event.title} (ID: {event.id?.substring(0, 8)}...)
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="w-full mt-4 p-3 bg-gray-50 rounded border border-gray-200">
              <p className="text-xs text-gray-600 font-semibold mb-2">
                Legacy Event Data Summary:
              </p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                <li><strong>Total Events:</strong> {legacyRecords.length}</li>
                <li><strong>Event Types:</strong> {[...new Set(legacyRecords.map(r => r.meetingDetails.EventType || "In-Person"))].join(', ')}</li>
                <li><strong>Setup Types:</strong> {[...new Set(legacyRecords.map(r => r.meetingDetails.SetupType || r.meetingDetails.MType || "N/A").filter(Boolean))].join(', ')}</li>
                <li><strong>Countries:</strong> {[...new Set(legacyRecords.map(r => r.meetingDetails.MCountry).filter(Boolean))].join(', ')}</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LegacyEventInserter;
